/**
 * LoginPage — email + password sign-in with forgot-password / reset flow
 */
import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import api from '../lib/apiClient';

// ── view states: 'login' | 'forgot' | 'reset' | 'reset_done'
export default function LoginPage() {
  const { login }       = useAuth();
  const navigate        = useNavigate();
  const [searchParams]  = useSearchParams();

  const [view, setView]     = useState('login');
  const [form, setForm]     = useState({ email: '', password: '', newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);
  const [success, setSuccess] = useState(null);
  const [resetToken, setResetToken] = useState(null);

  // Auto-switch to reset view if URL contains ?reset_token=...
  useEffect(() => {
    const tok = searchParams.get('reset_token');
    if (tok) {
      setResetToken(tok);
      setView('reset');
    }
  }, [searchParams]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  // ── Sign In ──────────────────────────────────────────────────────────────────
  const handleLogin = async e => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const user = await login({ email: form.email, password: form.password });
      navigate(user.charity_bn ? '/dashboard' : '/onboarding');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally { setLoading(false); }
  };

  // ── Forgot Password ──────────────────────────────────────────────────────────
  const handleForgot = async e => {
    e.preventDefault();
    setLoading(true); setError(null); setSuccess(null);
    try {
      await api.post('/auth/forgot-password', { email: form.email });
      setSuccess('If that email is registered you will receive a reset link shortly. Check your inbox (and spam folder).');
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setLoading(false); }
  };

  // ── Reset Password ───────────────────────────────────────────────────────────
  const handleReset = async e => {
    e.preventDefault();
    setError(null);
    if (form.newPassword !== form.confirmPassword) {
      setError('Passwords do not match.'); return;
    }
    if (form.newPassword.length < 8) {
      setError('Password must be at least 8 characters.'); return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token: resetToken, new_password: form.newPassword });
      setView('reset_done');
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset link is invalid or has expired.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white">
            IMPACT <span className="text-blue-400">PLEXUS</span>
          </h1>
          <p className="text-slate-400 mt-2">
            {view === 'login'      && 'Sign in to your account'}
            {view === 'forgot'     && 'Reset your password'}
            {view === 'reset'      && 'Choose a new password'}
            {view === 'reset_done' && 'Password updated'}
          </p>
        </div>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-8">

            {/* ── Sign In ── */}
            {view === 'login' && (
              <form onSubmit={handleLogin} className="space-y-5">
                {error && <ErrorBox msg={error} />}
                <Field id="email" label="Email" type="email" name="email"
                  value={form.email} onChange={handleChange} placeholder="you@yourorg.ca" autoFocus />
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-white">Password</Label>
                    <button
                      type="button"
                      onClick={() => { setError(null); setSuccess(null); setView('forgot'); }}
                      className="text-xs text-blue-400 hover:text-blue-300 hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Input id="password" name="password" type="password" required
                    value={form.password} onChange={handleChange}
                    className="bg-slate-700 border-slate-600 text-white" placeholder="••••••••" />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-blue-500 hover:bg-blue-600 text-white mt-2">
                  {loading ? 'Signing in…' : 'Sign In'}
                </Button>
                <p className="text-center text-sm text-slate-400">
                  Don't have an account?{' '}
                  <Link to="/onboarding" className="text-blue-400 hover:underline">Get started free</Link>
                </p>
              </form>
            )}

            {/* ── Forgot Password ── */}
            {view === 'forgot' && (
              <form onSubmit={handleForgot} className="space-y-5">
                {error   && <ErrorBox msg={error} />}
                {success && <SuccessBox msg={success} />}
                {!success && (
                  <>
                    <p className="text-sm text-slate-400">
                      Enter your account email and we'll send you a reset link.
                    </p>
                    <Field id="email" label="Email" type="email" name="email"
                      value={form.email} onChange={handleChange} placeholder="you@yourorg.ca" autoFocus />
                    <Button type="submit" disabled={loading} className="w-full bg-blue-500 hover:bg-blue-600 text-white">
                      {loading ? 'Sending…' : 'Send Reset Link'}
                    </Button>
                  </>
                )}
                <button type="button" onClick={() => { setError(null); setSuccess(null); setView('login'); }}
                  className="w-full text-center text-sm text-slate-400 hover:text-slate-300">
                  ← Back to sign in
                </button>
              </form>
            )}

            {/* ── Reset Password ── */}
            {view === 'reset' && (
              <form onSubmit={handleReset} className="space-y-5">
                {error && <ErrorBox msg={error} />}
                <p className="text-sm text-slate-400">Choose a new password for your account.</p>
                <Field id="newPassword" label="New Password" type="password" name="newPassword"
                  value={form.newPassword} onChange={handleChange} placeholder="Min 8 characters" autoFocus />
                <Field id="confirmPassword" label="Confirm Password" type="password" name="confirmPassword"
                  value={form.confirmPassword} onChange={handleChange} placeholder="Repeat password" />
                <Button type="submit" disabled={loading} className="w-full bg-blue-500 hover:bg-blue-600 text-white">
                  {loading ? 'Updating…' : 'Set New Password'}
                </Button>
              </form>
            )}

            {/* ── Reset Done ── */}
            {view === 'reset_done' && (
              <div className="space-y-5 text-center">
                <div className="text-4xl">✅</div>
                <p className="text-white font-medium">Password updated successfully!</p>
                <p className="text-slate-400 text-sm">You can now sign in with your new password.</p>
                <Button onClick={() => { setView('login'); navigate('/login', { replace: true }); }}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white">
                  Sign In
                </Button>
              </div>
            )}

          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function Field({ id, label, type, name, value, onChange, placeholder, autoFocus }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-white">{label}</Label>
      <Input id={id} name={name} type={type} required autoFocus={autoFocus}
        value={value} onChange={onChange}
        className="bg-slate-700 border-slate-600 text-white"
        placeholder={placeholder} />
    </div>
  );
}

function ErrorBox({ msg }) {
  return (
    <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg text-sm">{msg}</div>
  );
}

function SuccessBox({ msg }) {
  return (
    <div className="bg-green-500/10 border border-green-500/50 text-green-400 px-4 py-3 rounded-lg text-sm">{msg}</div>
  );
}
